import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { whenUnauthenticated } from '~/API/toolbox'

import { hasStoredAuthCredentials } from '.'
import { SessionExpiredContext, type SessionExpiredContextValue } from './SessionExpiredContext'

interface SessionExpiredProviderProps {
    children: ReactNode
}

/**
 * Provider that tracks session expiration at runtime.
 *
 * This listens for 401 responses from the API and sets isSessionExpired to true,
 * allowing components to show re-authentication UI.
 */
export const SessionExpiredProvider = ({ children }: SessionExpiredProviderProps) => {
    const [isSessionExpired, setSessionExpired] = useState(false)

    const handleSessionExpired = useCallback(() => {
        // Only mark as expired if we had credentials to begin with
        if (hasStoredAuthCredentials()) {
            setSessionExpired(true)
        }
    }, [])

    useEffect(() => {
        whenUnauthenticated(handleSessionExpired)
    }, [handleSessionExpired])

    const value: SessionExpiredContextValue = {
        isSessionExpired,
    }

    return <SessionExpiredContext.Provider value={value}>{children}</SessionExpiredContext.Provider>
}
